import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import BackButton from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BookOpen, ExternalLink, Search, Sparkles, ShoppingBag, Heart, Star, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BooksPage = () => {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [pinnedBooks, setPinnedBooks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storefrontUrl, setStorefrontUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchBooks();
    fetchPinnedBooks();
    fetchFavorites();
    fetchCategories();
    fetchStorefront();
  }, []);

  const fetchBooks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/books`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBooks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPinnedBooks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/books/pinned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPinnedBooks(response.data?.books || []);
    } catch (error) {
      console.error('Failed to fetch pinned books:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/books/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data?.favorites || []);
      if (response.data?.storefront_url) {
        setStorefrontUrl(response.data.storefront_url);
      }
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/books/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchStorefront = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/books/storefront`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorefrontUrl(response.data?.storefront_url || '');
    } catch (error) {
      console.error('Failed to fetch storefront:', error);
    }
  };

  // Filter books by category and search
  const filteredBooks = books.filter(book => {
    const matchesCategory = selectedCategory === 'all' || book.category_key === selectedCategory;
    const matchesSearch = !searchQuery || 
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group books by category for display
  const booksByCategory = {};
  filteredBooks.forEach(book => {
    const cat = book.category || 'Uncategorized';
    if (!booksByCategory[cat]) {
      booksByCategory[cat] = [];
    }
    booksByCategory[cat].push(book);
  });

  const categoryColors = {
    'Boundaries & Power': 'bg-purple-100 text-purple-700 border-purple-200',
    'Influence & Presence': 'bg-blue-100 text-blue-700 border-blue-200',
    'Energy & Wellness': 'bg-green-100 text-green-700 border-green-200',
    'Purpose & Vision': 'bg-amber-100 text-amber-700 border-amber-200',
    'Relationships & Connection': 'bg-pink-100 text-pink-700 border-pink-200',
    'Faith & Spirituality': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="books-page">
        <BackButton />
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-terracotta/20 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-terracotta" />
            </div>
            <div>
              <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">
                Recommended Reading
              </h1>
              <p className="text-gray-600">
                Curated by Crystal &amp; Dr. Nicole to support your journey
              </p>
            </div>
          </div>
        </div>

        {/* ====== PINNED: Books Written By Crystal & Dr. Nicole ====== */}
        {pinnedBooks.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-forest to-emerald-600 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-forest">
                  Books By Crystal &amp; Dr. Nicole
                </h2>
                <p className="text-sm text-gray-600">Get our books - written from our hearts to yours</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-5">
              {pinnedBooks.map((book, idx) => (
                <a
                  key={`pinned-${idx}`}
                  href={book.amazon_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-gradient-to-br from-forest/5 via-emerald-50 to-white rounded-xl border-2 border-forest/30 hover:border-forest hover:shadow-2xl transition-all group overflow-hidden relative"
                  data-testid={`pinned-book-${idx}`}
                >
                  {/* Pinned Badge */}
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-forest text-white border-0 text-xs font-semibold shadow-lg">
                      📌 Our Book
                    </Badge>
                  </div>
                  
                  <div className="p-6">
                    <div className="mb-3">
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                        Written by {book.written_by}
                      </Badge>
                    </div>
                    
                    <h3 className="font-heading text-lg font-bold text-forest group-hover:text-emerald-700 transition-colors mb-1 pr-16">
                      {book.title}
                    </h3>
                    {book.subtitle && (
                      <p className="text-sm font-medium text-emerald-600 mb-2">{book.subtitle}</p>
                    )}
                    <p className="text-sm text-gray-500 mb-3">by {book.author}</p>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-4">{book.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-forest group-hover:text-emerald-700">
                        Get Your Copy →
                      </span>
                      <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-forest" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Amazon Storefront CTA */}
        {storefrontUrl && (
          <Card className="card-base mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
                    <ShoppingBag className="h-7 w-7 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-forest">
                      Crystal &amp; Dr. Nicole&apos;s Amazon Storefront
                    </h3>
                    <p className="text-sm text-gray-600">
                      Browse our complete collection of recommended resources
                    </p>
                  </div>
                </div>
                <a
                  href={storefrontUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
                >
                  Visit Storefront <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Crystal & Dr. Nicole's Favorites Section */}
        {favorites.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-terracotta to-rose-500 flex items-center justify-center">
                <Heart className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-forest">
                  Crystal &amp; Dr. Nicole&apos;s Favorites
                </h2>
                <p className="text-sm text-gray-600">The books that transformed our lives &amp; practice</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {favorites.map((book, idx) => (
                <a
                  key={`favorite-${idx}`}
                  href={book.amazon_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-gradient-to-br from-cream to-white rounded-xl border-2 border-terracotta/30 hover:border-terracotta hover:shadow-xl transition-all group overflow-hidden"
                  data-testid={`favorite-book-${idx}`}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Badge className="bg-terracotta/10 text-terracotta border-terracotta/20 text-xs">
                        <Star className="h-3 w-3 mr-1 fill-terracotta" />
                        {book.recommended_by}
                      </Badge>
                      <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-terracotta flex-shrink-0" />
                    </div>
                    
                    <h3 className="font-heading font-semibold text-forest group-hover:text-terracotta transition-colors mb-1 line-clamp-2">
                      {book.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">by {book.author}</p>
                    
                    {book.why_we_love_it && (
                      <div className="bg-terracotta/5 rounded-lg p-3 border-l-3 border-terracotta">
                        <div className="flex items-start gap-2">
                          <Quote className="h-4 w-4 text-terracotta flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-700 italic line-clamp-3">
                            {book.why_we_love_it}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        {favorites.length > 0 && (
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-sand"></div>
            <span className="text-sm text-gray-500 font-medium">All Recommended Books</span>
            <div className="h-px flex-1 bg-sand"></div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search books by title, author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-base"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-forest hover:bg-forest/90' : ''}
            >
              All
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.key}
                variant={selectedCategory === cat.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.key)}
                className={selectedCategory === cat.key ? 'bg-forest hover:bg-forest/90' : ''}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-forest border-t-transparent rounded-full" />
          </div>
        )}

        {/* Books Grid by Category */}
        {!loading && Object.keys(booksByCategory).length === 0 && (
          <Card className="card-base">
            <CardContent className="p-12 text-center">
              <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No books found matching your criteria</p>
            </CardContent>
          </Card>
        )}

        {!loading && Object.entries(booksByCategory).map(([category, categoryBooks]) => (
          <div key={category} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="h-5 w-5 text-terracotta" />
              <h2 className="font-heading text-xl font-semibold text-forest">{category}</h2>
              <span className="text-sm text-gray-500">({categoryBooks.length} books)</span>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryBooks.map((book, idx) => (
                <a
                  key={`${book.title}-${idx}`}
                  href={book.amazon_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-5 bg-white rounded-xl border border-sand hover:border-terracotta/50 hover:shadow-lg transition-all group"
                  data-testid={`book-card-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-medium text-forest group-hover:text-terracotta transition-colors line-clamp-2">
                      {book.title}
                    </h3>
                    <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-terracotta flex-shrink-0" />
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-2">by {book.author}</p>
                  <p className="text-sm text-gray-600 line-clamp-3 mb-3">{book.description}</p>
                  
                  {book.focus_areas && book.focus_areas.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {book.focus_areas.slice(0, 2).map(area => (
                        <Badge 
                          key={area} 
                          variant="outline" 
                          className={`text-xs ${categoryColors[category] || 'bg-gray-100 text-gray-700'}`}
                        >
                          {area}
                        </Badge>
                      ))}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        ))}

        {/* Bottom CTA */}
        {!loading && storefrontUrl && (
          <div className="text-center mt-8 py-8 border-t border-sand">
            <p className="text-gray-600 mb-4">
              Looking for more? Visit our complete Amazon storefront for additional resources.
            </p>
            <a
              href={storefrontUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-terracotta hover:text-terracotta/80 font-medium"
            >
              Browse Full Collection <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BooksPage;
